<template>
  <NuxtLayout
    v-if="activeTab"
    name="banner-trip"
  >
    <LazyRolintLink
      class="m-1 absolute"
      :trip-id="trip.id"
      text="EDIT TRIP"
    />
    <LazyDetailsTab
      v-if="tripTab === 'details' || tripTab === 'info'"
      :itineraries="itineraryArr || []"
      :trip="trip"
      :latestreviews="latestReviews"
      :suggested="suggested"
      :calendar="calendar"
    />
    <LazyTestimonialsTab
      v-else-if="tripTab === 'reviews'"
      :reviews-average="bannerReviews"
      :reviews="reviews"
      :ratings-chart="ratingsChart"
      :loading="pending"
    />
    <LazyFilmTab
      v-else-if="tripTab === 'films'"
      :films="films"
      :loading="pending"
    />
    <LazyBedplaceTab
      v-else-if="tripTab === 'hotels' || tripTab === 'ships'"
      :hotels="hotels"
      :cruiseships="cruiseships"
      :loading="pending"
    />
    <LazyFaqTab
      v-else-if="tripTab === 'faq'"
      :faq="faq"
      :loading="pending"
    />
    <LazyDeparturesTab
      v-else-if="tripTab === 'calendar'"
      :loading="pending"
      :calendar="calendar"
      :flight-trip="!!trip.flight_trip"
    />
  </NuxtLayout>
</template>

<script setup>
// import events from '@layers/web/constants/events.js'
import { callWithNuxt } from '#app'

import { NID_REGEXP } from '@layers/web/constants/regexp.js'
import events from '@layers/web/constants/events.js'

definePageMeta({
  scrollToTop: false,
  validate: route => [route.params.triptype, route.params.slug]
    .every(param => param && NID_REGEXP.test(param.trim())),
})

const nuxtApp = useNuxtApp()
// const { $event } = nuxtApp
const { trackViewContent } = useTracking()
const route = useRoute()
const { $sentryCaptureMessage, $sentrySetContext, $sentryCaptureException } = useNuxtApp()
const { t } = useI18n()
const router = useRouter()

const tripStore = useTripStore()
const localeStore = useLocaleStore()
const menuStore = useMenuStore()

const {
  trip,
  itinerary,
  reviewsForTriptype: reviews,
  hotelsTriptypes: hotels,
  cruiseshipsTriptypes: cruiseships,
  faqTriptypes: faq,
  filmsTriptypes: films,
  ratingsChart,
  calendar,
  suggestedTriptypes: suggested,
  tripPath,
  tripTab,
} = storeToRefs(tripStore)

// const movie = computed(() => {
//   return {
//     movie: trip.value.main_movie ? trip.value.main_movie : '',
//     type: trip.value.main_movie_type ? trip.value.main_movie_type : ''
//   }
// })

const itineraryArr = computed(() => {
  if (!itinerary.value) {
    return []
  }

  return Object.values(itinerary.value)
})

const latestReviews = computed(() => {
  if (!trip.value.reviews_samples) {
    return []
  }
  const latestReviewsArr = []
  trip.value.reviews_samples.forEach((review, index) => {
    if (review && index < 2) {
      latestReviewsArr.push(review)
    }
  })

  return latestReviewsArr
})

const bannerReviews = computed(() => {
  return typeof (trip.value.reviews_average) === 'object'
    ? trip.value.reviews_average
    : {}
})

const { setPageBanner } = useBanner()

const { error, data } = await useAsyncData('trip', async () => {
  try {
    const tripName = encodeURIComponent(route.params.slug)
    const tripUrlId = `${route.params.triptype}/${tripName}`

    // Convert localized tab
    const urls = localeStore.urls.tripSlug
    const tab = findActiveSlugKey(urls, route.params)

    const prevPath = tripPath.value
    const newPath = '/' + tripUrlId
    const isNewTrip = prevPath !== newPath

    const data = {
      activeTab: tab,
      isNewTrip, //never used outside of this function
      shouldScrollTop: isNewTrip,
      loadSuggested: isNewTrip,
      tripUrlId,
    }

    if (isNewTrip) {
      try {
        const { data: trip } = await apiFetch(`/${localeStore.locale}/triptype?url=${tripUrlId}`)

        if (!trip) {
          throw new Error('Post not found: ' + router.currentRoute.value.fullPath)
        }

        const calendarInit = await tripStore.fetchCalendar({ url: tripUrlId })

        const params = route.params

        if (!trip?.sections) {
          $sentrySetContext('data', JSON.stringify({ params, trip }))
          $sentryCaptureMessage('Trip sections empty', {
            level: 'warning',
            tags: {
              type: 'UX',
            },
          })
          throw new Error('Post not found: ' + router.currentRoute.value.fullPath)
        }

        if (!calendarInit?.departures || calendarInit.error) {
          $sentrySetContext('data', JSON.stringify({ params, trip, calendarInit }))
          $sentryCaptureMessage('Trip calendar response empty', {
            level: 'warning',
            tags: {
              type: 'UX',
            },
          })
          throw new Error('Post not found: ' + router.currentRoute.value.fullPath)
        }

        const hasDepartures = !!calendarInit?.departures?.length

        const [itinerary] = await Promise.all([
          tripStore.fetchItinerary(trip.id),
          tripStore.fetchSuggestedTriptypes(trip.id),
        ])

        if (!itinerary.error) {
          tripStore.SET_ITINERARY(itinerary)
        }

        const daysText = !hasDepartures ? null : t('tripDaysText', { n: days(trip) })
        if (trip.reviews_average?.avg) {
          trip.reviews_average.avg = parseFloat(trip.reviews_average?.avg?.toFixed(2))
        }
        tripStore.SET_TRIP({
          ...trip,
          daysText,
          hasDepartures,
        })
        tripStore.INIT_TRIP_VISITED()
        tripStore.SET_TRIP_PATH(newPath)
      } catch (e) {
        if (e.response?.status === 404) {
          if (e.response._data?.redirectTo) {
            callWithNuxt(nuxtApp, navigateTo, [e.response._data.redirectTo])
            return
          }

          try {
            const { data: redirectInfo } = await apiFetch(`/${localeStore.locale}/redirect?url=${newPath}`)

            if (redirectInfo.redirectTo) {
              callWithNuxt(nuxtApp, navigateTo, [redirectInfo.redirectTo])
            }
          } catch (e) {
          // do nothing
          }
        }
        $sentryCaptureException(e)
        throw new Error('Post not found: ' + router.currentRoute.value.fullPath)
      }
    }

    const trip = tripStore?.trip
    const isDiscounted = trip.price_min < trip.price_min_without_discount

    let heroIcon
    if (trip.ships_exists) {
      heroIcon = 'ship'
    } else if (trip.flight_trip) {
      heroIcon = 'plane'
    } else {
      heroIcon = 'bus'
    }

    setPageBanner({
      title: trip.title,
      background: {
        image: {
          url: trip.main_picture_cloudinary,
          gravity: trip.main_picture_cloudinary_alignment,
        },
      },
      type: 'rectangle-container',
      mobileType: 'bottom-container',
      stars: !trip.reviews_average ? {} : {
        score: trip.reviews_average.avg,
        reviewCount: formatNumber(trip.reviews_num ?? trip.reviews_average.count),
      },
      linkButton: !trip.hasDepartures ? null : {
        text: t('bookTitle'),
      },
      price: trip.price_min,
      priceWithoutDiscount: isDiscounted ? trip.price_min_without_discount : null,
      days: trip.daysText,
      special: {
        type: 'icon',
        data: {
          icon: heroIcon,
        },
      },
      ...(
        trip.id === 1980 ? {
          special: {
            type: 'bookmark',
            data: {
              text: t('firstCome'),
            },
          },
        } : {}
      ),
    })

    return data
  } catch (e) {
    throw new Error('Post not found: ' + router.currentRoute.value.fullPath)
  }
})

if (error.value) {
  // Quickfix site SEO score
  if (isSol()) {
    navigateTo(`${localeStore.urls.campaign}/gruppresor`)
  }

  throw createError({
    message: error.value.message,
    statusCode: 404,
    stack: error.value.stack,
    fatal: true,
  })
}

const { activeTab, shouldScrollTop, loadSuggested, tripUrlId } = data.value

const { pending } = await useAsyncData(async () => {
  if (tripStore.tripTabVisited[activeTab.value]) {
    return
  }

  const tripId = trip.value.id

  // Tab switching + cached data loading
  switch (activeTab) {
    case 'reviews':
      await Promise.all([
        tripStore.fetchReviewsForTriptype(tripId),
        tripStore.fetchRatingsChart(tripId),
      ])
      break
    case 'hotels':
    case 'ships':
      await tripStore.fetchBedplacesForTriptype(tripId)
      break
    case 'films':
      await tripStore.fetchFilmsForTriptype(tripId)
      break
    case 'faq':
      await tripStore.fetchFaqForTriptype(tripId)
      break
  }

  tripStore.SET_TRIP_VISITED({ tabName: activeTab, value: true })
  return {
    pending,
  }
})

useHead({
  title: () => `${trip.value.title}`,
})

onBeforeMount(() => {
  if (shouldScrollTop && process.browser) {
    window.scrollTo(0, 0)
  }
})

const refreshCalendar = async () => {
  await tripStore.fetchCalendar({ url: tripUrlId })
}

const { $event } = useNuxtApp()

onMounted(() => {
  if (trip.value && loadSuggested.value) {
    menuStore.closeSideMenuActive()
  }

  trackViewContent({
    name: trip.value.title,
    value: trip.value.price_min,
    id: trip.value.id,
  })

  $event.$on(events.calendarRefresh, refreshCalendar)
})

//eventlistner never fiers
onBeforeUnmount(() => {
  $event.$off(events.calendarRefresh, refreshCalendar)
})

// const onLoadingStateChange = (v) => {
//   if (v) {
//     console.log('loading', v)
//     isExiting.value = true
//   }
// }
</script>
